import type {
  TransferApprovalCondition as APITransferApprovalCondition,
  TransferApprovalRule as APITransferApprovalRule,
  UpdateTransferApprovalConfigRequest as APIUpdateTransferApprovalConfigRequest,
} from '@column/api';

import { PlatformType } from '~/models/PlatformModel';
import { InternationalWireBillingConfig } from '~/typings/API';
import { convertValues } from '~/util';

import { client } from './client';
import { AddressType } from './EntityRepository';
import { GoogleFile } from './GoogleFileRepository';
import { financialProductType, financialProducts } from './Platform/financialProducts';

export interface GetIntlWireConfigRequest {
  platformId?: string;
}

export interface GetPlatformRequest {
  platformId: string;
}

export interface UpdatePlatformRequest {
  platformId: string;
  name?: string;
  reportingTimeZone?: string;
  enableLive?: boolean;
}

export type TransferApprovalTransferType = APITransferApprovalCondition['transfer_types'][number];

export interface TransferApprovalCondition {
  amountGte?: APITransferApprovalCondition['amount_gte'];
  transferTypes: APITransferApprovalCondition['transfer_types'];
}

export interface TransferApprovalRule {
  condition: TransferApprovalCondition;
  priority: APITransferApprovalRule['priority'];
}

export type TransferApprovalRules = TransferApprovalRule[];

export interface UpdatePlatformConfigRequest {
  platformId: string;
  transferApprovalConfig?: {
    enabledForAchCredits?: APIUpdateTransferApprovalConfigRequest['enabled_for_ach_credits'];
    enabledForAchDebits?: APIUpdateTransferApprovalConfigRequest['enabled_for_ach_debits'];
    enabledForCheckDebits?: APIUpdateTransferApprovalConfigRequest['enabled_for_check_debits'];
    enabledForInternationalWire?: APIUpdateTransferApprovalConfigRequest['enabled_for_international_wire'];
    enabledForWire?: APIUpdateTransferApprovalConfigRequest['enabled_for_wire'];
    enabledForRealtime?: APIUpdateTransferApprovalConfigRequest['enabled_for_realtime'];
    rules?: TransferApprovalRules | null;
  };
  issuerProcessingDashboardEnabled?: boolean;
}

export type UpdatePlatform = UpdatePlatformRequest;
export type { financialProductType, InternationalWireBillingConfig };

export { financialProducts };

export type BusinessPageIdType = 'information' | 'location' | 'payment-volume' | 'your-customers' | 'your-information';

export interface PersonType {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  jobTitle?: string;
  ownershipPercentage?: string;
  dateOfBirth?: string;
  email?: string;
  verificationType?: 'ssn' | 'itin' | 'passport';
  ssn?: string;
  itin?: string;
  countryOfIssue?: string;
  passportNumber?: string;
  dateOfIssue?: string;
  dateOfExpiration?: string;
  passportDocumentId?: string;
  address?: AddressType;
  documentaryProof?: GoogleFile;
}

export interface PlatformConfig {
  id: string;
  checkIssuingCustomCheckNumbersEnabled?: boolean;
  checkIssuingMailingEnabled?: boolean;
  transferApprovalConfig?: {
    enabledForAchCredits?: boolean;
    enabledForAchDebits?: boolean;
    enabledForCheckDebits?: boolean;
    enabledForInternationalWire?: boolean;
    enabledForWire?: boolean;
    enabledForRealtime?: boolean;
    locked?: boolean;
    rules?: TransferApprovalRules | null;
  };
  issuerProcessingDashboardEnabled?: boolean;
  correspondentBankingEnabled?: boolean;
}

export interface PlatformMeta {
  companyName?: string;
  signUpPurpose?: string;
  goLiveDate?: string;
  financialProductsSelected?: financialProductType[];
  financialProductsText?: string;
  financialProductsSubmitted?: boolean;
  companySize?: string;
  customersSize?: string;
  otherComment?: string;
  contactNumber?: string;
  readyToLaunch?: boolean;
  complianceCapabilities?: string;
  selfPaymentOrigination?: string;
  businessInformationSubmitted?: boolean;
  esignPolicyAccepted?: boolean;
  depositAccountAgreementAccepted?: boolean;
  commercialPurposesAcked?: boolean;
  paymentOriginationAccepted?: boolean;
  customerTypes?: string[];
  customerTypeOther?: string;
  contactToRemoveLimits?: boolean;
  businessTypes?: string[];
  moveMoneyFor?: string[];
  docSubmittedPreRootEntityCreation?: string[];

  // dependency graph node values for onboarding
  depgraphNodeValues?: DepgraphNodeValues;
}

export interface DepgraphNodeValues {
  completedPages?: BusinessPageIdType[];
  clientInstanceId?: string;
  // business information
  customerType?: string;
  entityLegalName?: string;
  entityDbaName?: string;
  entityWebsite?: string;
  legalEntityType?: string;
  entityStateOfIncorporation?: string;
  entityYearOfIncorporation?: string;
  entityCountryOfIncorporation?: string;
  entityRegistrationId?: string;
  sourceOfFunds?: string[];
  descriptionOfPrimarySourcesOfFunds?: string;
  einProof?: GoogleFile;
  entityDetailsDocumentaryProof?: GoogleFile;
  evidenceOfActiveStatus?: GoogleFile;
  businessDocumentationProof?: GoogleFile;
  bankAccountAuthorizationDocument?: GoogleFile;

  // business location
  locationsOfBusinessOperation?: string[];
  entityPhysicalAddress?: AddressType;
  entityPhysicalAddressProof?: GoogleFile;
  entityRegisteredAddress?: AddressType;
  entityMailingAddress?: AddressType;

  // business customers
  typesOfCustomers?: string[];
  locationsOfCustomers?: string[];
  naicsCode?: string;
  businessDescription?: string;

  // account opener, control person, beneficial owners
  accountOpener?: PersonType;
  entityAccountOpenerIsControlPerson?: boolean;
  entityAccountOpenerIsBeneficialOwner?: boolean;
  controlPerson?: PersonType;
  entityControlPersonIsBeneficialOwner?: boolean;
  beneficialOwners?: PersonType[];

  // submit page
  submitted?: boolean;
}

export class PlatformRepository {
  static async get(id: string) {
    return client
      .get<GetPlatformRequest, PlatformType>(`/dashboard/platforms/${id}`, {
        sandbox: true,
        platformId: id,
      })
      .then((response) => convertValues<PlatformType>(response));
  }

  static async update(request: UpdatePlatformRequest) {
    return client
      .patch<UpdatePlatformRequest, PlatformType>(`/dashboard/platforms/${request.platformId}`, request, {
        platformId: request.platformId,
        sandbox: false,
      })
      .then((response) => convertValues<PlatformType>(response));
  }

  static async updateMeta(platformId: string, request: PlatformMeta) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { depgraphNodeValues, ...requestPayload } = request;
    return client.patch<PlatformMeta, PlatformMeta>(`/dashboard/platforms/${platformId}/metadata`, requestPayload, {
      platformId,
      sandbox: false,
    });
  }

  static async getConfig(platformId: string) {
    return client.get<any, PlatformConfig>('/dashboard/platforms/config', {
      platformId,
    });
  }

  static async getMeta(platformId: string) {
    return client.get<any, PlatformMeta>(`/dashboard/platforms/${platformId}/metadata`, {
      platformId,
      sandbox: false,
    });
  }

  static async getInternationalWireConfig(request?: GetIntlWireConfigRequest) {
    return client
      .get<
        string,
        InternationalWireBillingConfig
      >('/transfers/international-wire/config', { platformId: request?.platformId })
      .then((response) => convertValues<InternationalWireBillingConfig>(response));
  }

  static async updateConfig({ platformId, ...request }: UpdatePlatformConfigRequest) {
    return client.patch<Omit<UpdatePlatformConfigRequest, 'platformId'>, PlatformConfig>(
      '/dashboard/platforms/config',
      request,
      {
        allowNumbersInRequest: true,
        platformId,
      }
    );
  }

  static async updateInternationalWireConfig(request: { platformId?: string } & InternationalWireBillingConfig) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { platformId, ...requestPayload } = request;
    return client
      .post<InternationalWireBillingConfig, InternationalWireBillingConfig>(
        '/transfers/international-wire/config',
        requestPayload,
        {
          allowNumbersInRequest: true,
          platformId: request.platformId,
        }
      )
      .then((response) => convertValues<InternationalWireBillingConfig>(response));
  }

  static async updateDependencyGraph(request: DepgraphNodeValues) {
    return client.patch<DepgraphNodeValues, DepgraphNodeValues>('/dashboard/onboarding/depgraph', request, {
      sandbox: false,
      allowNumbersInRequest: true,
    });
  }

  static async getDependencyGraph(platformId: string) {
    return client.get<unknown, DepgraphNodeValues>('/dashboard/onboarding/depgraph', {
      platformId,
      sandbox: false,
    });
  }
}
